import { Typography } from '@mui/material'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import { useFormik } from 'formik'
import { FC } from 'react'
import { useLocation } from 'react-router-dom'
import * as yup from 'yup'
import { useDiskStore } from '../../store/storeDisk'
import { useFilesStore } from '../../store/storeFiles'
import { File, ObjectType } from '../../swagger/dtoGateway'
import { getFileName, getFileType } from '../../utils/files'

const validationSchema = yup.object({
    name: yup
        .string()
        .trim()
        .matches(
            /^[^~!@\/:\\#$%^&*=|`"<>]*$/,
            'Название не должно содержать символы ~!@/:#$%^&*=|`\"<>',
        )
        .max(100, 'Название может быть не более 100 символов')
        .required('Название обязательное'),
})

const getDisplayName = (fileName: string, type?: ObjectType) => {
    if (type === ObjectType.Folder) {
        return (
            fileName
                ?.split('/')
                .filter((value) => !!value)
                .pop() ?? ''
        )
    }

    return getFileName(fileName)
}

const renameFileInUrl = (url: string, newFileName: string) => {
    const originFileExtension = getFileType(url)

    const lastSlashIndex = url.lastIndexOf('/')
    const finalFileName = originFileExtension
        ? `${newFileName}.${originFileExtension}`
        : newFileName

    return `${url.slice(0, lastSlashIndex + 1)}${finalFileName}`
}

type Props = {
    open: boolean
    handleClose: VoidFunction
    file: File
    idEntity: string
}

export const FileRenameDialog: FC<Props> = ({ open, handleClose, file, idEntity }) => {
    const { moveFileOrFolder } = useDiskStore((state) => state)
    const { fetchFilesUser } = useFilesStore((state) => state)
    const { pathname } = useLocation()

    const formik = useFormik({
        initialValues: {
            name: '',
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const prefix = decodeURI(file.filename?.split(file.user_uuid).pop() ?? '')
            if (pathname === '/disk') {
                if (file.object_type === ObjectType.Folder) {
                    const array = prefix.split('/')
                    const index = array.length - 2
                    const folder = array[index]
                    const destination = decodeURI(prefix.replace(folder, values.name))
                    await moveFileOrFolder({ prefix, destination })
                    handleClose()
                } else {
                    const destination = decodeURI(renameFileInUrl(prefix, values.name))
                    const folder = decodeURI(file.url?.split('disk').pop() ?? '').split(prefix)[0]
                    await moveFileOrFolder({
                        prefix: `/\disk${folder}${prefix}`,
                        destination: `/\disk${folder}${destination}`,
                    })
                    handleClose()
                }
            } else {
                const destination = renameFileInUrl(prefix, values.name)
                await moveFileOrFolder({
                    prefix: `/${idEntity}/${prefix}`,
                    destination: `/${idEntity}/${destination}`,
                })
                await fetchFilesUser({ prefix: `/${idEntity}/` })
                handleClose()
            }
        },
    })

    const displayName = getDisplayName(file.filename, file.object_type)

    return (
        <Dialog
            slotProps={{
                paper: {
                    style: {
                        overflow: 'hidden',
                        width: '560px',
                    },
                },
            }}
            open={open}
            onClose={handleClose}>
            <DialogTitle>
                Переименовать {file.object_type === ObjectType.Folder ? 'папку' : 'файл'}
            </DialogTitle>

            <Typography
                variant="h4"
                sx={{
                    ml: 4,
                }}>
                Текущее имя:
            </Typography>

            <Typography
                variant="body2"
                sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    ml: 4,
                }}>
                {displayName}
            </Typography>
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <TextField
                        fullWidth
                        id="name"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.name && Boolean(formik.errors.name)}
                        helperText={formik.touched.name && formik.errors.name}
                        variant="outlined"
                        label="Название"
                    />
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleClose} variant="outlined">
                        Отменить
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={!(formik.isValid && formik.dirty)}>
                        Сохранить
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    )
}
